import React, {
  useMemo,
  useState,
  useCallback,
  useRef,
  useEffect,
} from "react";
import { useVirtualizer } from "@tanstack/react-virtual";
import { InformationCircleIcon } from "@heroicons/react/24/outline";
import type {
  DiseaseTaxonomyNode,
  TreeNode,
} from "../../types/diseaseTaxonomy.types";
import { buildTree, getAllDescendantIds } from "../../utils/diseaseTaxonomyTree";
import {
  getDiseaseDisplayName,
  getDiseaseTooltipWithMondo,
  diseaseNameMatchesSearchQuery,
} from "../../utils/diseaseDisplayName";

interface DiseaseAutocompleteProps {
  nodes: DiseaseTaxonomyNode[];
  selectedIds: Set<number>;
  onSelectionChange: (ids: Set<number>) => void;
  allowSingleSelection?: boolean;
}

const ROW_HEIGHT = 34;
const INDENT_PX = 18;
const MAX_DROPDOWN_HEIGHT = 320;

const DiseaseAutocomplete: React.FC<DiseaseAutocompleteProps> = ({
  nodes,
  selectedIds,
  onSelectionChange,
  allowSingleSelection = true,
}) => {
  const [query, setQuery] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const scrollRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handle = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener("mousedown", handle);
    return () => document.removeEventListener("mousedown", handle);
  }, []);

  const treeRoots = useMemo(() => buildTree(nodes), [nodes]);

  const treeNodeMap = useMemo(() => {
    const m = new Map<number, TreeNode>();
    const walk = (list: TreeNode[]) => {
      for (const n of list) {
        m.set(n.id, n);
        walk(n.children);
      }
    };
    walk(treeRoots);
    return m;
  }, [treeRoots]);

  // Build ancestor map for each node
  const ancestorMap = useMemo(() => {
    const m = new Map<number, Set<number>>();
    for (const n of nodes) {
      const ancestors = new Set<number>();
      let pid = n.parent_id;
      while (pid != null) {
        ancestors.add(pid);
        const parent = nodes.find((x) => x.id === pid);
        pid = parent?.parent_id ?? null;
      }
      m.set(n.id, ancestors);
    }
    return m;
  }, [nodes]);

  // Check if any ancestor is selected
  const isAncestorSelected = useCallback(
    (nodeId: number): boolean => {
      const ancestors = ancestorMap.get(nodeId);
      if (!ancestors) return false;
      for (const aid of ancestors) {
        if (selectedIds.has(aid)) return true;
      }
      return false;
    },
    [ancestorMap, selectedIds]
  );

  // Filtered results: matching nodes + ancestors + descendants
  const filteredRows = useMemo(() => {
    const q = query.toLowerCase().trim();
    if (!q) return [] as { node: DiseaseTaxonomyNode; isDirectMatch: boolean }[];

    const matchedIds = new Set<number>();
    const keepIds = new Set<number>();

    const parentMap = new Map<number, number>();
    for (const n of nodes) {
      if (n.parent_id != null) parentMap.set(n.id, n.parent_id);
    }

    for (const n of nodes) {
      if (diseaseNameMatchesSearchQuery(n.name, q)) {
        matchedIds.add(n.id);
        keepIds.add(n.id);
        let pid = n.parent_id;
        while (pid != null && !keepIds.has(pid)) {
          keepIds.add(pid);
          pid = parentMap.get(pid) ?? null;
        }
        const treeNode = treeNodeMap.get(n.id);
        if (treeNode && treeNode.children.length > 0) {
          for (const cid of getAllDescendantIds(treeNode)) {
            keepIds.add(cid);
          }
        }
      }
    }

    const rows: { node: DiseaseTaxonomyNode; isDirectMatch: boolean }[] = [];
    for (const n of nodes) {
      if (keepIds.has(n.id)) {
        rows.push({ node: n, isDirectMatch: matchedIds.has(n.id) });
      }
    }
    return rows;
  }, [query, nodes, treeNodeMap]);

  const virtualizer = useVirtualizer({
    count: filteredRows.length,
    getScrollElement: () => scrollRef.current,
    estimateSize: () => ROW_HEIGHT,
    overscan: 10,
  });

  const toggleNode = useCallback(
    (nodeId: number) => {
      const node = nodes.find((n) => n.id === nodeId);
      if (!node) return;

      if (node.depth === 0) return;
      if (isAncestorSelected(nodeId)) return;

      if (selectedIds.has(nodeId)) {
        const next = new Set(selectedIds);
        next.delete(nodeId);
        onSelectionChange(next);
        return;
      }

      // Single selection mode: replace previous selection
      if (allowSingleSelection) {
        onSelectionChange(new Set([nodeId]));
        return;
      }

      const next = new Set(selectedIds);
      const treeNode = treeNodeMap.get(nodeId);

      if (treeNode && treeNode.children.length > 0) {
        const descIds = getAllDescendantIds(treeNode);
        for (const did of descIds) {
          if (did !== nodeId) next.delete(did);
        }
      }
      next.add(nodeId);
      onSelectionChange(next);
    },
    [nodes, selectedIds, onSelectionChange, treeNodeMap, isAncestorSelected, allowSingleSelection]
  );

  const getCheckState = useCallback(
    (nodeId: number): "checked" | "unchecked" | "indeterminate" => {
      if (selectedIds.has(nodeId)) return "checked";
      if (isAncestorSelected(nodeId)) return "checked";

      const treeNode = treeNodeMap.get(nodeId);
      if (treeNode && treeNode.children.length > 0) {
        const descIds = getAllDescendantIds(treeNode);
        const hasSelectedDesc = descIds.some((id) => id !== nodeId && selectedIds.has(id));
        if (hasSelectedDesc) return "indeterminate";
      }

      return "unchecked";
    },
    [selectedIds, treeNodeMap, isAncestorSelected]
  );

  return (
    <div ref={containerRef} className="relative w-full">
      <div className="relative">
        <input
          ref={inputRef}
          type="text"
          value={query}
          onChange={(e) => {
            setQuery(e.target.value);
            setIsOpen(true);
          }}
          onFocus={() => {
            if (query.trim()) setIsOpen(true);
          }}
          placeholder="Search diseases..."
          className="w-full px-3 py-2 pr-8 text-sm border border-slate-300 dark:border-slate-600 rounded-lg bg-white dark:bg-slate-700 text-slate-800 dark:text-white placeholder-slate-400 focus:ring-2 focus:ring-blue-500 focus:border-blue-500 outline-none transition"
        />
        <svg
          className="absolute right-2.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
        </svg>
      </div>

      {isOpen && query.trim() && (
        <div className="absolute left-0 z-50 mt-1 w-[420px] bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-600 rounded-xl shadow-xl overflow-hidden" style={{ top: "100%" }}>
          {filteredRows.length === 0 ? (
            <div className="px-4 py-3 text-sm text-slate-500 dark:text-slate-400">
              No matching diseases
            </div>
          ) : (
            <>
              <div className="px-3 py-1.5 text-[10px] text-slate-400 dark:text-slate-500 border-b border-slate-100 dark:border-slate-700">
                {filteredRows.length} result{filteredRows.length !== 1 ? "s" : ""}
              </div>
              <div
                ref={scrollRef}
                style={{ maxHeight: MAX_DROPDOWN_HEIGHT }}
                className="overflow-auto"
              >
                <div
                  style={{ height: virtualizer.getTotalSize(), position: "relative" }}
                >
                  {virtualizer.getVirtualItems().map((vi) => {
                    const { node, isDirectMatch } = filteredRows[vi.index];
                    const displayName = getDiseaseDisplayName(node.name);
                    const checkState = getCheckState(node.id);
                    const hasChildren = !node.is_leaf && node.child_count > 0;
                    const isRoot = node.depth === 0;
                    const isLocked = isAncestorSelected(node.id);
                    const isSelectedParent = selectedIds.has(node.id) && hasChildren;

                    return (
                      <div
                        key={node.id}
                        data-index={vi.index}
                        ref={virtualizer.measureElement}
                        title={getDiseaseTooltipWithMondo(node.name, node.mondo_id)}
                        className={`absolute top-0 left-0 w-full flex items-center px-3 transition-colors ${
                          isLocked
                            ? "cursor-not-allowed bg-slate-100 dark:bg-slate-700/50"
                            : isRoot
                            ? "cursor-not-allowed"
                            : "cursor-pointer hover:bg-blue-50 dark:hover:bg-slate-700/50"
                        } ${isDirectMatch && !isLocked ? "bg-yellow-50/50 dark:bg-yellow-900/10" : ""}`}
                        style={{
                          height: ROW_HEIGHT,
                          transform: `translateY(${vi.start}px)`,
                        }}
                        onClick={() => toggleNode(node.id)}
                      >
                        <div style={{ width: node.depth * INDENT_PX }} className="shrink-0" />

                        <input
                          type="checkbox"
                          checked={checkState === "checked"}
                          disabled={isRoot || isLocked}
                          ref={(el) => {
                            if (el) el.indeterminate = checkState === "indeterminate";
                          }}
                          onChange={() => toggleNode(node.id)}
                          onClick={(e) => e.stopPropagation()}
                          className={`w-3.5 h-3.5 rounded focus:ring-blue-500 focus:ring-offset-0 shrink-0 mr-2 ${
                            isRoot
                              ? "cursor-not-allowed opacity-50 border-slate-300 dark:border-slate-500 text-blue-600"
                              : isLocked
                              ? "cursor-not-allowed opacity-60 border-slate-400 dark:border-slate-500 text-slate-500 dark:text-slate-400"
                              : "cursor-pointer border-slate-300 dark:border-slate-500 text-blue-600"
                          }`}
                        />

                        <span
                          className={`text-sm truncate flex-1 min-w-0 ${
                            isDirectMatch
                              ? "font-semibold text-blue-700 dark:text-blue-300"
                              : isRoot
                              ? "font-medium text-slate-500 dark:text-slate-400"
                              : isLocked
                              ? "text-slate-400 dark:text-slate-500"
                              : "text-slate-700 dark:text-slate-200"
                          }`}
                          title={
                            displayName === node.name
                              ? node.name
                              : `${displayName} — ${node.name}`
                          }
                        >
                          {displayName}
                        </span>

                        {hasChildren && (
                          <span className="ml-1 text-[10px] text-slate-400 dark:text-slate-500 shrink-0">
                            ({node.child_count})
                          </span>
                        )}

                        {isSelectedParent && (
                          <span
                            className="ml-2 shrink-0 p-0.5 bg-red-100 dark:bg-red-900/40 rounded"
                            title="Children are auto-selected. To select individual children, uncheck this parent first."
                          >
                            <InformationCircleIcon className="w-4 h-4 text-red-500 dark:text-red-400" />
                          </span>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </div>
  );
};

export default React.memo(DiseaseAutocomplete);
